///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useSelector } from "react-redux";
import { orderBy } from "lodash";

import { status, bidStatusOptions, order } from "../helpers/types";
import Loader from "./home/active-bids-grid-view/Loader";
import TableBody from "./home/active-bids-grid-view/TableBody";
import TableHeader from "./home/active-bids-grid-view/TableHeader";
import getLaneCount from "../helpers/getLaneCount";
import getDeadlineDate from "../helpers/getDeadlineDate";
import getStartDate from "../helpers/getStartDate";
import getLastStatusChangeDate from "../helpers/getLastStatusChangeDate";
import getLeastProgressedMode from "../helpers/getLeastProgressedMode";
import getActiveBidAvatars from "../helpers/getActiveBidAvatars";
import getPricingRegionUsersAvatars from "../helpers/getPricingRegionUsersAvatars";

const getFormattedBids = (bids, status, user) => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const bidStatus = bidStatusOptions.find(({ value }) => value === status);
  return bids
    .filter(b => b.matchesSearch)
    .map(b => {
      const activeBidAvatars = getActiveBidAvatars(b);
      const pricingRegionUsersAvatars = getPricingRegionUsersAvatars(b);
      return {
        ...b,
        status: bidStatus,
        bidStatusId: bidStatus.value,
        formattedDeadlineDate: getDeadlineDate(b, user, timeZone),
        formattedStartDate: getStartDate(b, user, timeZone),
        laneCount: getLaneCount(b.modes),
        formattedLastChangeTime: getLastStatusChangeDate(b, user, timeZone),
        lastStatusChangeDateUTC: getLeastProgressedMode(b.modes, user)
          .lastStatusChangeDateUTC,
        activeBidAvatars,
        pricingRegionUsersAvatars,
        teamCount: activeBidAvatars.length,
        assignmentsCount: pricingRegionUsersAvatars.length
      };
    });
};

const reverseOrder = sortOrder =>
  sortOrder === order.ascending ? order.descending : order.ascending;

const ActiveBidsGridView = ({ activeBids }) => {
  const { user } = useSelector(state => state.user);
  const { field, order } = activeBids.sort;
  const filteredActiveBids = orderBy(
    [
      ...getFormattedBids(activeBids.pendingBids, status.pending, user),
      ...getFormattedBids(activeBids.inProgressBids, status.inProgress, user),
      ...getFormattedBids(activeBids.submittedBids, status.submitted, user),
      ...getFormattedBids(activeBids.feedbackBids, status.feedback, user)
    ],
    [
      bid =>
        bid[field] && bid[field].toLowerCase
          ? bid[field].toLowerCase()
          : bid[field]
    ],
    [field === "lastStatusChangeDateUTC" ? reverseOrder(order) : order]
  );

  return (
    <article
      id="active-bids"
      className="flex flex-col"
      data-testid="active-bids-grid-view"
    >
      <div className="flex flex-auto flex-wrap justify-between  pos-relative active-bids-grid-view">
        {activeBids.isFetching ? (
          <Loader isFetching={activeBids.isFetching} />
        ) : (
          <table className="one-whole bids-grid">
            <TableHeader sortField={field} sortOrder={order} />
            {filteredActiveBids && filteredActiveBids.length > 0 && (
              <TableBody bids={filteredActiveBids} user={user} />
            )}
          </table>
        )}
      </div>
    </article>
  );
};

export default ActiveBidsGridView;
